import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { DialView } from '../components/DialView';
import { PresetButtons } from '../components/PresetButtons';
import { PresetEditorModal } from '../components/PresetEditorModal';
import { ScheduleCard } from '../components/ScheduleCard';
import { ScheduleEditorModal } from '../components/ScheduleEditorModal';
import {
  SetPumpClockModal,
  formatDisplay as formatPumpClockDisplay,
} from '../components/SetPumpClockModal';
import { StatusDot } from '../components/StatusDot';
import { FollowingBadge } from '../components/FollowingBadge';
import { ApiError, fnViaAlias, getFnViaAlias } from '../lib/apiClient';
import { isUserPresent, onPresenceResume } from '../lib/presence';
import { openPumpStateStream } from '../lib/sseClient';
import type { Device, SpeedSetting } from '../lib/types';

/**
 * Pump detail — `/pump/:deviceId`. Phase 7 wires the power toggle,
 * preset-select buttons, preset RPM editor (modal), and schedule
 * editor (modal). Editing is owner-only — followers see a disabled
 * pencil button with a tooltip.
 *
 * Live updates: a per-pump SSE subscription with `?deviceId=<id>` so
 * the bus only delivers events for the pump in view. State events
 * trigger a deviceInfo refetch; presence events flip
 * `connectionStatus.isConnected` optimistically.
 */
export function PumpDetail() {
  const { deviceId = '' } = useParams<{ deviceId: string }>();
  const [device, setDevice] = useState<Device | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sseConnected, setSseConnected] = useState(false);
  const [busy, setBusy] = useState<null | 'toggle' | 'speed-step' | `speed:${string}`>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Which editor modal is open ('preset' | 'schedule' | null). The
  // modals are owner-only — followers see disabled pencils.
  const [editorOpen, setEditorOpen] = useState<
    null | 'preset' | 'schedule' | 'pump-clock'
  >(null);

  const reload = useCallback(async () => {
    if (!deviceId) return;
    setError(null);
    try {
      const dev = await getFnViaAlias<Device>(
        `/devices/device-info/${encodeURIComponent(deviceId)}`,
      );
      setDevice(dev);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Unable to load this pump.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Presence heartbeat (cost Optimization 1): while this pump's detail page is
  // open AND a user is actually present, tell the backend a user is watching so
  // presence-gated polling keeps it on the fast cadence. watch_ttl defaults to
  // ~30s server-side, so ping every 15s. Gated on isUserPresent() (tab visible
  // + recent interaction) so an open-but-unattended tab doesn't pin the pump to
  // the fast cadence indefinitely — when pings stop, the lease lapses and the
  // pump idles. onPresenceResume re-pings immediately when the user returns.
  // Best-effort — a missed ping just lets the pump fall back to idle.
  useEffect(() => {
    if (!deviceId) return;
    const ping = () => {
      if (!isUserPresent()) return;
      void fnViaAlias('PUT', '/devices/watch', { deviceId }).catch(() => {
        /* presence is an optimization; ignore */
      });
    };
    ping();
    const id = window.setInterval(ping, 15000);
    const off = onPresenceResume(ping);
    return () => {
      window.clearInterval(id);
      off();
    };
  }, [deviceId]);

  // Per-pump SSE subscription + staleness watchdog.
  //
  // Per ADR-007 the server derives connectionStatus from
  // iot_devices.last_seen_at on every read (90s threshold). Live state
  // arrives via the pumpState SSE stream; the SPA refetches deviceInfo
  // on each `state` event so the rest of the response stays fresh.
  //
  // BUT: if the pump dies, EPV-PUMP-UP stops, no state events fire, and
  // the SPA's cached `connectionStatus.isConnected` stays true forever
  // even though the server would now derive offline. We dropped the
  // pump_offline server-side event in Phase 1.4 (switched to
  // derive-on-read) so we can't lean on it.
  //
  // Watchdog: track the last time we saw any SSE event. If >60s pass
  // without one, force a reload(). The server-side derivation flips
  // to offline once last_seen_at age > 90s; our 60s wakeup ticks
  // gives it ~30s of headroom to flip on the next refetch.
  useEffect(() => {
    if (!deviceId) return;
    const conn = openPumpStateStream({
      deviceId,
      onOpen: () => setSseConnected(true),
      onError: () => setSseConnected(false),
      onEvent: (event) => {
        if (event.type === 'state') {
          void reload();
          return;
        }
        if (
          event.type === 'pump_online' ||
          event.type === 'pump_first_connected' ||
          event.type === 'pump_offline'
        ) {
          // A presence transition — refetch so connectionStatus comes from the
          // server's authoritative last_seen derivation, not an optimistic flip.
          void reload();
        }
      },
    });
    // Unconditional periodic refetch so the server's last_seen-based connection
    // derivation (and command-confirmation status) stays fresh even when the
    // pump goes silent — e.g. switched to AP/direct mode. No 'state' events
    // arrive while a pump is offline, so event-driven reloads alone can leave
    // the cached `isConnected` stuck true (showing "Idle" when it's offline).
    const watchdog = setInterval(() => {
      void reload();
    }, 30_000);
    return () => {
      conn.close();
      clearInterval(watchdog);
    };
  }, [deviceId, reload]);

  // Command-confirmation watchdog — warn once if the pump didn't apply the last
  // control command within its deadline (online but ignoring writes). Keyed off
  // commandAt so it warns once per command, not on every state push.
  const warnedKey = useRef<string | null>(null);
  useEffect(() => {
    if (device?.commandStatus !== 'unconfirmed' || !device.commandAt) return;
    // Tailor the message to connectivity (the same shape read that produced
    // commandStatus also carries connectionStatus, so they're consistent): an
    // offline pump never got the command; an online one got it but isn't
    // applying writes. Re-evaluate if connectivity flips for the same command
    // (e.g. it went offline after the warning), keyed so it doesn't spam.
    const connected = device.connectionStatus?.isConnected ?? false;
    const key = `${device.commandAt}|${connected}`;
    if (warnedKey.current === key) return;
    warnedKey.current = key;
    setActionError(
      connected
        ? "The pump didn't confirm the command — it's online but isn't responding to controls. Try again, or power-cycle the pump."
        : "The pump didn't confirm the command — it appears to be offline, so the command couldn't be delivered. Check the pump's connection (it may be in AP/direct mode or powered down).",
    );
  }, [device?.commandStatus, device?.commandAt, device?.connectionStatus?.isConnected]);

  async function onToggle() {
    if (!device || busy) return;
    setActionError(null);
    setBusy('toggle');
    const newStatus = device.deviceStatus === 'ON' ? 'OFF' : 'ON';
    // Optimistic UI flip — the SSE state event will reconcile the
    // authoritative shape moments later.
    setDevice((prev) =>
      prev ? { ...prev, deviceStatus: newStatus } : prev,
    );
    try {
      await fnViaAlias<string>('PUT', '/devices/toggle', {
        deviceId,
        status: newStatus,
      });
      // Don't await reload — SSE will push the next state.
    } catch (err) {
      // Roll back the optimistic flip and show the error.
      setDevice((prev) =>
        prev ? { ...prev, deviceStatus: device.deviceStatus } : prev,
      );
      setActionError(
        err instanceof Error ? err.message : 'Unable to toggle the pump.',
      );
    } finally {
      setBusy(null);
    }
  }

  /**
   * Curved-arrow speed-step handler — mirrors the iOS DialView
   * `drawChangeSpeedButton` action block. +100 / -100 RPM with
   * dead-zone skip: if the result lands between 0 and `minMotorSpeed`
   * (the pump can't physically run there) we snap UP to
   * `minMotorSpeed` when stepping up, or DOWN to 0 when stepping
   * down. That keeps the buttons usable across the full RPM range
   * without parking the pump in the unsafe sub-800 RPM band.
   */
  async function onSpeedStep(delta: number) {
    if (!device || busy) return;
    const MAX_RPM = 3400;
    const MIN_MOTOR_SPEED = 800;
    const current = typeof device.currentSpeed === 'number'
      ? device.currentSpeed
      : 0;
    let next = current + delta;
    if (next > MAX_RPM) next = MAX_RPM;
    if (next < 0) next = 0;
    // Skip the dead-zone (0, minMotorSpeed)
    if (next > 0 && next < MIN_MOTOR_SPEED) {
      next = delta > 0 ? MIN_MOTOR_SPEED : 0;
    }
    if (next === current) return;

    setActionError(null);
    setBusy('speed-step');
    const previousSpeed = device.currentSpeed;
    // Optimistic — needle sweeps to the new value while the command
    // is in flight. The next SSE state event reconciles authoritative.
    setDevice((prev) =>
      prev ? { ...prev, currentSpeed: next } : prev,
    );
    try {
      await fnViaAlias<null>('PUT', '/devices/change-current-speed', {
        deviceId,
        speed: next,
      });
    } catch (err) {
      // Roll back the optimistic update.
      setDevice((prev) =>
        prev ? { ...prev, currentSpeed: previousSpeed } : prev,
      );
      setActionError(
        err instanceof Error
          ? err.message
          : 'Unable to change the pump speed.',
      );
    } finally {
      setBusy(null);
    }
  }

  async function onSelectSpeed(slot: keyof SpeedSetting) {
    if (!device || busy) return;
    setActionError(null);
    setBusy(`speed:${slot}`);
    const previous = {
      selectedSpeed: device.selectedSpeed,
      deviceStatus: device.deviceStatus,
    };
    // Optimistic — selecting a preset implicitly turns the pump ON
    // (per the handler's documented side-effect).
    setDevice((prev) =>
      prev
        ? { ...prev, selectedSpeed: slot, deviceStatus: 'ON' }
        : prev,
    );
    try {
      await fnViaAlias<null>('PUT', '/devices/select-current-speed', {
        deviceId,
        speedName: slot,
      });
    } catch (err) {
      // Roll back.
      setDevice((prev) => (prev ? { ...prev, ...previous } : prev));
      setActionError(
        err instanceof Error
          ? err.message
          : 'Unable to change the speed preset.',
      );
    } finally {
      setBusy(null);
    }
  }

  const isOn = device?.deviceStatus === 'ON';
  const isConnected = device?.connectionStatus?.isConnected ?? false;
  const isPriming =
    isOn &&
    !!device?.selectedSpeed &&
    !['S1', 'S2', 'S3', 'S4'].includes(device.selectedSpeed);
  // Disable controls when offline (no point pretending to send commands)
  // OR when this user is following (only owners can control today; the
  // server-side authorization check would 403 anyway).
  const canControl =
    !!device && isConnected && device.ownership !== 'following';

  return (
    <>
      <AppHeader />
      <main className="container py-4" style={{ maxWidth: 720 }}>
        <p className="mb-3">
          <Link to="/dashboard">← Back to my pumps</Link>
        </p>

        {loading && !device && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {device && (
          <>
            <header className="mb-4 d-flex flex-wrap align-items-baseline gap-3">
              <h2 className="h3 mb-0" style={{ color: 'var(--aqua-text-strong)' }}>
                {device.deviceName || device.deviceId}
              </h2>
              {device.ownership === 'following' && (
                <FollowingBadge ownerDisplayName={device.ownerDisplayName} />
              )}
              <span
                className="ms-auto small"
                style={{
                  color: sseConnected
                    ? 'var(--aqua-online)'
                    : 'var(--aqua-text-muted)',
                }}
              >
                {sseConnected ? '● Live' : '○ Reconnecting…'}
              </span>
            </header>

            <section
              className="card mb-3"
              style={{
                borderColor: 'var(--aqua-border)',
                boxShadow: 'var(--aqua-shadow-sm)',
              }}
            >
              <div className="card-body text-center pt-4 pb-3">
                <DialView
                  currentRpm={device.currentSpeed}
                  isOn={isOn}
                  isConnected={isConnected}
                  currentWatt={device.currentWatt}
                  isPriming={isPriming}
                  onPowerToggle={canControl ? onToggle : undefined}
                  powerBusy={busy === 'toggle'}
                  onSpeedStep={canControl ? onSpeedStep : undefined}
                  speedStepBusy={busy === 'speed-step'}
                />
                <div className="mt-3 d-flex align-items-center justify-content-center">
                  <StatusDot connected={isConnected} on={isOn} />
                </div>

                {actionError && (
                  <div
                    className="alert alert-warning mt-3 mb-0 py-2 small"
                    role="alert"
                  >
                    {actionError}
                  </div>
                )}

                {!canControl && (
                  <div className="small text-muted mt-3">
                    {device.ownership === 'following'
                      ? 'View-only — only the pump owner can change settings.'
                      : 'Pump is offline — controls are disabled.'}
                  </div>
                )}
              </div>
            </section>

            <section
              className="card mb-3"
              style={{
                borderColor: 'var(--aqua-border)',
                boxShadow: 'var(--aqua-shadow-sm)',
              }}
            >
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h3
                    className="h6 mb-0"
                    style={{ color: 'var(--aqua-text-strong)' }}
                  >
                    Speed presets
                  </h3>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setEditorOpen('preset')}
                    disabled={device.ownership === 'following'}
                    title={
                      device.ownership === 'following'
                        ? 'Only the pump owner can edit presets'
                        : 'Change the RPM stored in each preset slot'
                    }
                  >
                    Edit RPMs
                  </button>
                </div>
                <PresetButtons
                  device={device}
                  disabled={!canControl}
                  busySlot={
                    busy && busy.startsWith('speed:')
                      ? (busy.split(':')[1] as keyof SpeedSetting)
                      : null
                  }
                  onSelect={(slot) => void onSelectSpeed(slot)}
                />
                {device.ownership === 'following' ? null : (
                  <div className="form-text mt-2">
                    Tap a preset to switch the pump to that speed.
                  </div>
                )}
              </div>
            </section>

            <section
              className="card mb-3"
              style={{
                borderColor: 'var(--aqua-border)',
                boxShadow: 'var(--aqua-shadow-sm)',
              }}
            >
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h3
                    className="h6 mb-0"
                    style={{ color: 'var(--aqua-text-strong)' }}
                  >
                    Schedule
                  </h3>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setEditorOpen('schedule')}
                    disabled={device.ownership === 'following'}
                    title={
                      device.ownership === 'following'
                        ? 'Only the pump owner can edit the schedule'
                        : 'Edit the daily schedule slots'
                    }
                  >
                    Edit schedule
                  </button>
                </div>
                <div className="d-flex flex-column gap-2">
                  {device.deviceSchedules?.map((s, i) => (
                    <ScheduleCard key={s.scheduleId} schedule={s} index={i} />
                  ))}
                </div>
              </div>
            </section>

            <section
              className="card"
              style={{
                borderColor: 'var(--aqua-border)',
                boxShadow: 'var(--aqua-shadow-sm)',
              }}
            >
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h3
                    className="h6 mb-0"
                    style={{ color: 'var(--aqua-text-strong)' }}
                  >
                    Pump info
                  </h3>
                  {/* Pump-clock setter — owner-only. Disabled when pump
                      is offline since the firmware can't receive the
                      MQTT set_clock frame. Mirrors iOS DeviceDetails
                      "Set Pump Clock" button (DeviceDetailsView.swift
                      line 205). */}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setEditorOpen('pump-clock')}
                    disabled={!canControl}
                    title={
                      device.ownership === 'following'
                        ? 'Only the pump owner can set the clock'
                        : !isConnected
                          ? 'Pump must be online to set the clock'
                          : 'Set the pump clock'
                    }
                  >
                    Set pump clock
                  </button>
                </div>
                <dl className="row mb-0 small">
                  <dt className="col-sm-4 text-muted">Hardware ID</dt>
                  <dd className="col-sm-8">
                    <code style={{ color: 'var(--aqua-text)' }}>
                      {device.deviceId}
                    </code>
                  </dd>
                  {device.modelNumber && (
                    <>
                      <dt className="col-sm-4 text-muted">Model</dt>
                      <dd className="col-sm-8">{device.modelNumber}</dd>
                    </>
                  )}
                  <dt className="col-sm-4 text-muted">Pump clock</dt>
                  <dd className="col-sm-8" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatPumpClockDisplay(device.timeSetting)}
                  </dd>
                  {device.ownership === 'following' && (
                    <>
                      <dt className="col-sm-4 text-muted">Owner</dt>
                      <dd className="col-sm-8">
                        {device.ownerDisplayName ||
                          device.ownerEmail ||
                          'Unknown'}
                      </dd>
                    </>
                  )}
                </dl>
              </div>
            </section>
          </>
        )}
      </main>

      {/* Editor modals — owner-only; followers can't reach the trigger
          button. Each closes on save and triggers a deviceInfo reload
          so the new values render straight away. */}
      {editorOpen === 'preset' && device ? (
        <PresetEditorModal
          device={device}
          onClose={() => setEditorOpen(null)}
          onSaved={() => void reload()}
        />
      ) : null}
      {editorOpen === 'schedule' && device ? (
        <ScheduleEditorModal
          device={device}
          onClose={() => setEditorOpen(null)}
          onSaved={() => void reload()}
        />
      ) : null}
      {editorOpen === 'pump-clock' && device ? (
        <SetPumpClockModal
          device={device}
          onClose={() => setEditorOpen(null)}
          onSaved={() => void reload()}
        />
      ) : null}
    </>
  );
}
